import React, { useEffect, useState } from 'react';
import Layout from './layout';
import {
  getTrainers,
  createTrainer,
  updateTrainer,
  deleteTrainer,
} from './api/trainerApi';
import apiClient from './api/apiClient';

function TrainersPage() {
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ email: '', full_name: '', phone: '' });
  const [editId, setEditId] = useState(null);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [selectedTrainer, setSelectedTrainer] = useState(null);
  const [courseAssignments, setCourseAssignments] = useState([]);
  const [assignmentLoading, setAssignmentLoading] = useState(false);

  const fetchTrainers = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getTrainers();
      setTrainers(data.trainers || data); // support both array and {trainers: []}
    } catch (err) {
      setError('Failed to fetch trainers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrainers();
  }, []);

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAdd = () => {
    setFormData({ email: '', full_name: '', phone: '' });
    setEditId(null);
    setShowForm(true);
  };

  const handleEdit = (trainer) => {
    setFormData({
      email: trainer.User?.email || trainer.email || '',
      full_name: trainer.User?.full_name || trainer.full_name || '',
      phone: trainer.User?.phone || trainer.phone || '',
    });
    setEditId(trainer.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this trainer?')) return;
    try {
      await deleteTrainer(id);
      fetchTrainers();
    } catch {
      setError('Failed to delete trainer');
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editId) {
        await updateTrainer(editId, formData);
      } else {
        await createTrainer(formData);
      }
      setShowForm(false);
      fetchTrainers();
    } catch {
      setError('Failed to save trainer');
    }
  };

  const handleAssignToCourse = async (trainer) => {
    setSelectedTrainer(trainer);
    setShowAssignmentModal(true);
    setAssignmentLoading(true);
    try {
      const response = await apiClient(`/trainers/${trainer.id}/courses/assignment-status`);
      setCourseAssignments(response.data || response || []);
    } catch (err) {
      setError('Failed to fetch course assignments');
    } finally {
      setAssignmentLoading(false);
    }
  };

  const handleAssignmentToggle = async (courseId, currentAssigned) => {
    try {
      const newAssigned = currentAssigned ? 0 : 1;
      await apiClient(`/trainers/${selectedTrainer.id}/courses/assignment-status`, {
        method: 'POST',
        body: {
          courseId: courseId,
          assigned: newAssigned
        }
      });
      
      // Update local state
      setCourseAssignments(prev => 
        prev.map(course => 
          course.id === courseId 
            ? { ...course, assigned: newAssigned }
            : course
        )
      );
    } catch (err) {
      setError('Failed to update course assignment');
    }
  };

  return (
    <Layout>
      <div className="container p-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2>Trainers</h2>
          <button className="btn btn-primary" onClick={handleAdd}>Add Trainer</button>
        </div>
        {error && <div className="alert alert-danger">{error}</div>}
        {loading ? (
          <div>Loading...</div>
        ) : (
          <table className="table table-bordered table-hover">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {trainers.length === 0 && (
                <tr><td colSpan={4} className="text-center">No trainers found.</td></tr>
              )}
              {trainers.map((trainer) => {
                const name = trainer.User?.full_name || trainer.full_name;
                const email = trainer.User?.email || trainer.email;
                const phone = trainer.User?.phone || trainer.phone;
                // Australian phone formatting
                let formattedPhone = phone;
                if (typeof phone === 'string') {
                  const digits = phone.replace(/\D/g, '');
                  if (digits.length === 10) {
                    if (["02","03","07","08"].includes(digits.slice(0,2))) {
                      // Mobile: XX XXXX XXXX
                      formattedPhone = `${digits.slice(0,2)}-${digits.slice(2,6)}-${digits.slice(6,10)}`;
                    } else if (digits.startsWith('04') || digits.startsWith('05')) {
                      // Landline: XXXX-XXX-XXX
                      formattedPhone = `${digits.slice(0,4)}-${digits.slice(4,7)}-${digits.slice(7,10)}`;
                    }
                  }
                }
                return (
                  <tr key={trainer.id}>
                    <td>{name}</td>
                    <td>{email ? <a href={`mailto:${email}`}>{email}</a> : ''}</td>
                    <td>{formattedPhone}</td>
                    <td>
                      <button className="btn btn-sm btn-primary me-2" onClick={() => handleAssignToCourse(trainer)}>Assign Course</button>
                      <button className="btn btn-sm btn-secondary me-2" onClick={() => handleEdit(trainer)}>Edit</button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(trainer.id)}>☠️</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Modal/Form for Add/Edit */}
        {showForm && (
          <div className="modal show d-block" tabIndex="-1" role="dialog" style={{background: 'rgba(0,0,0,0.3)'}}>
            <div className="modal-dialog" role="document">
              <div className="modal-content">
                <form onSubmit={handleFormSubmit}>
                  <div className="modal-header">
                    <h5 className="modal-title">{editId ? 'Edit Trainer' : 'Add Trainer'}</h5>
                    <button type="button" className="btn-close" onClick={() => setShowForm(false)} aria-label="Close"></button>
                  </div>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">Full Name</label>
                      <input type="text" className="form-control" name="full_name" value={formData.full_name} onChange={handleInputChange} required />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Email</label>
                      <input type="email" className="form-control" name="email" value={formData.email} onChange={handleInputChange} required />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Phone</label>
                      <input type="text" className="form-control" name="phone" value={formData.phone} onChange={handleInputChange} required />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
                    <button type="submit" className="btn btn-primary">Save</button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Course Assignment Modal */}
        {showAssignmentModal && (
          <div className="modal show d-block" tabIndex="-1" role="dialog" style={{background: 'rgba(0,0,0,0.3)'}}>
            <div className="modal-dialog modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    Assign Courses to {selectedTrainer?.User?.full_name || selectedTrainer?.full_name}
                  </h5>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setShowAssignmentModal(false)} 
                    aria-label="Close"
                  ></button>
                </div>
                <div className="modal-body">
                  {assignmentLoading ? (
                    <div className="text-center">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2">Loading course assignments...</p>
                    </div>
                  ) : courseAssignments.length === 0 ? (
                    <p className="text-center text-muted">No courses available for assignment.</p>
                  ) : (
                    <div className="row">
                      {courseAssignments.map((course) => (
                        <div key={course.id} className="col-md-6 mb-3">
                          <div className="card">
                            <div className="card-body d-flex justify-content-between align-items-center">
                              <div>
                                <h6 className="card-title mb-1">{course.name || course.title}</h6>
                                {course.description && (
                                  <small className="text-muted">{course.description}</small>
                                )}
                              </div>
                              <div className="form-check form-switch">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`course-${course.id}`}
                                  checked={course.assigned === 1 || course.assigned === true}
                                  onChange={() => handleAssignmentToggle(course.id, course.assigned === 1 || course.assigned === true)}
                                />
                                <label className="form-check-label" htmlFor={`course-${course.id}`}>
                                  {course.assigned === 1 || course.assigned === true ? 'Assigned' : 'Not Assigned'}
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowAssignmentModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default TrainersPage; 